//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';

import { CAMPAIGN_SUPPORT_EMAIL } from '@campaigns/constants/SupportContactConstants';
import { PrimaryAnchor } from '@components/atoms/anchor/Anchor';
import { TrackingId } from '@models/TrackingId';
import { isTruthy } from '@utils/filterPredicates';

export interface SupportRequestContext {
  campaignName?: string;
  requestShortCode?: string;
}

interface ContactSupportProps {
  /** Copy rendered immediately before the link, e.g. 'If the problem continues, email'. */
  leadingCopy?: string;
  /** Link text. Defaults to the support address itself. */
  linkText?: string;
  /** Request details prefilled into the email when the caller knows them. */
  requestContext?: SupportRequestContext;
  trackingId: TrackingId;
}

const getSupportMailtoHref = ({
  campaignName,
  requestShortCode,
}: SupportRequestContext) => {
  const requestDetails = [
    requestShortCode && `Request ID: ${requestShortCode}`,
    campaignName && `Campaign name: ${campaignName}`,
  ].filter(isTruthy);

  if (requestDetails.length === 0) {
    return `mailto:${CAMPAIGN_SUPPORT_EMAIL}`;
  }

  const subject = isTruthy(requestShortCode)
    ? `Campaign request support — ${requestShortCode}`
    : 'Campaign request support';
  const body = `I need help with this campaign request.\n\n${requestDetails.join('\n')}`;

  return `mailto:${CAMPAIGN_SUPPORT_EMAIL}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;
};

/**
 * Renders a mailto: link to Bazaarvoice campaign support, prefilled with the
 * campaign request the client is looking at when that is known.
 */
export const ContactSupport = ({
  leadingCopy,
  linkText = CAMPAIGN_SUPPORT_EMAIL,
  requestContext = {},
  trackingId,
}: ContactSupportProps) => {
  return (
    <>
      {!!leadingCopy && `${leadingCopy} `}
      <PrimaryAnchor
        href={getSupportMailtoHref(requestContext)}
        trackingId={trackingId}
      >
        {linkText}
      </PrimaryAnchor>
    </>
  );
};
